import { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Filter, UserPlus, Shield, Ban, CheckCircle,
  MoreVertical, Eye, Edit, Trash2, Server, Mail, Calendar,
  ChevronDown, X, AlertTriangle, Crown, UserCheck
} from 'lucide-react';
import { api } from '@/lib/api-client';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (roleFilter) params.append('role', roleFilter);
      if (planFilter) params.append('plan', planFilter);
      
      const data = await api.get(`/api/auth/admin/users/?${params.toString()}`);
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, roleFilter, planFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleViewUser = async (userId) => {
    try {
      const data = await api.get(`/api/auth/admin/users/${userId}/`);
      setSelectedUser(data);
      setShowUserModal(true);
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
    setActionDropdown(null);
  };

  const handleToggleStatus = async (user) => {
    try {
      await api.put(`/api/auth/admin/users/${user.id}/update/`, {
        is_active: !user.is_active
      });
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
    }
    setActionDropdown(null);
  };

  const handleToggleSuperuser = async (user) => {
    if (!window.confirm(`Are you sure you want to ${user.is_superuser ? 'remove' : 'grant'} superuser privileges for ${user.email}?`)) {
      return;
    }
    try {
      await api.put(`/api/auth/admin/users/${user.id}/update/`, {
        is_superuser: !user.is_superuser
      });
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to update user');
    }
    setActionDropdown(null);
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to disable ${user.email}? This will prevent them from logging in.`)) {
      return;
    }
    try {
      await api.delete(`/api/auth/admin/users/${user.id}/delete/`);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to delete user');
    }
    setActionDropdown(null);
  };

  const getRoleBadge = (user) => {
    if (user.is_superuser) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1"><Crown size={12} /> Superuser</span>;
    }
    if (user.is_admin || user.is_staff) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1"><Shield size={12} /> Admin</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-zinc-500/20 text-zinc-400">User</span>;
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-400 flex items-center gap-1"><CheckCircle size={12} /> Active</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-500/20 text-red-400 flex items-center gap-1"><Ban size={12} /> Disabled</span>;
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-zinc-500/20 text-zinc-400',
      starter: 'bg-blue-500/20 text-blue-400',
      professional: 'bg-emerald-500/20 text-emerald-400',
      enterprise: 'bg-purple-500/20 text-purple-400',
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${colors[plan] || colors.free}`}>{plan}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="text-emerald-400" /> User Management
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Manage users, permissions, and access</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-black font-medium rounded-lg transition-colors"
        >
          <UserPlus size={18} /> Create Superuser
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name, or company..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${showFilters ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}
          >
            <Filter size={18} /> Filters {(statusFilter || roleFilter || planFilter) && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
          </button>
        </div>

        {/* Filter dropdowns */}
        {showFilters && (
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Disabled</option>
            </select>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All Roles</option>
              <option value="superuser">Superusers</option>
              <option value="admin">Admins</option>
              <option value="user">Regular Users</option>
            </select>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All Plans</option>
              <option value="free">Free Trial</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
            {(statusFilter || roleFilter || planFilter) && (
              <button
                onClick={() => { setStatusFilter(''); setRoleFilter(''); setPlanFilter(''); }}
                className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"
              >
                <X size={14} /> Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* User count */}
      <div className="text-sm text-zinc-500">
        Showing {users.length} user{users.length !== 1 ? 's' : ''}
      </div>

      {/* Users Table */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Instances</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Joined</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-zinc-500">
                    <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
                    <p className="mt-2">Loading users...</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-zinc-500">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                          {(user.full_name || user.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.full_name || 'No name'}</p>
                          <p className="text-zinc-500 text-sm">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">{getRoleBadge(user)}</td>
                    <td className="px-4 py-4">{getStatusBadge(user.is_active)}</td>
                    <td className="px-4 py-4">{getPlanBadge(user.plan)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Server size={14} />
                        <span>{user.instance_count || 0}</span>
                        {user.running_instances > 0 && (
                          <span className="text-emerald-400 text-xs">({user.running_instances} running)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-zinc-500 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="relative flex justify-end">
                        <button
                          onClick={() => setActionDropdown(actionDropdown === user.id ? null : user.id)}
                          className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                        >
                          <MoreVertical size={16} className="text-zinc-400" />
                        </button>
                        {actionDropdown === user.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 py-1">
                            <button
                              onClick={() => handleViewUser(user.id)}
                              className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              <Eye size={14} /> View Details
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user)}
                              className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              {user.is_active ? <Ban size={14} /> : <CheckCircle size={14} />}
                              {user.is_active ? 'Disable User' : 'Enable User'}
                            </button>
                            <button
                              onClick={() => handleToggleSuperuser(user)}
                              className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              <Crown size={14} />
                              {user.is_superuser ? 'Remove Superuser' : 'Make Superuser'}
                            </button>
                            <div className="border-t border-zinc-700 my-1" />
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => { setShowUserModal(false); setSelectedUser(null); }} onRefresh={fetchUsers} />
      )}

      {/* Create Superuser Modal */}
      {showCreateModal && (
        <CreateSuperuserModal onClose={() => setShowCreateModal(false)} onSuccess={fetchUsers} />
      )}
    </div>
  );
}

// User Detail Modal Component
function UserDetailModal({ user, onClose, onRefresh }) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    is_active: user.is_active,
    is_superuser: user.is_superuser,
    plan: user.plan,
    credit_balance: user.credit_balance,
  });

  const handleSave = async () => {
    try {
      await api.put(`/api/auth/admin/users/${user.id}/update/`, formData);
      onRefresh();
      setEditMode(false);
    } catch (err) {
      alert(err.message || 'Failed to update user');
    }
  };

  const handleInstanceAction = async (instanceId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this instance?`)) return;
    try {
      await api.post(`/api/auth/admin/instances/${instanceId}/${action}/`);
      alert(`Instance ${action} initiated`);
    } catch (err) {
      alert(err.message || 'Failed to perform action');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold">
              {(user.full_name || user.email)[0].toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{user.full_name || 'No name'}</h2>
              <p className="text-zinc-400">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">User Information</h3>
              
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Status</label>
                    <select
                      value={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="true">Active</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Role</label>
                    <select
                      value={formData.is_superuser}
                      onChange={(e) => setFormData({ ...formData, is_superuser: e.target.value === 'true' })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="false">Regular User</option>
                      <option value="true">Superuser</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Plan</label>
                    <select
                      value={formData.plan}
                      onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="free">Free Trial</option>
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Credit Balance</label>
                    <input
                      type="number"
                      value={formData.credit_balance}
                      onChange={(e) => setFormData({ ...formData, credit_balance: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSave} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-black font-medium rounded-lg">Save</button>
                    <button onClick={() => setEditMode(false)} className="px-4 py-2 border border-zinc-700 text-zinc-300 rounded-lg hover:bg-zinc-800">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Status</span>
                    <span className={user.is_active ? 'text-emerald-400' : 'text-red-400'}>{user.is_active ? 'Active' : 'Disabled'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Role</span>
                    <span className="text-white">{user.role}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Plan</span>
                    <span className="text-white capitalize">{user.plan}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Credit Balance</span>
                    <span className="text-white">${parseFloat(user.credit_balance || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Company</span>
                    <span className="text-white">{user.company || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">Joined</span>
                    <span className="text-white">{new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-zinc-400">OAuth Provider</span>
                    <span className="text-white capitalize">{user.oauth_provider || 'Email'}</span>
                  </div>
                  <button onClick={() => setEditMode(true)} className="w-full mt-4 px-4 py-2 border border-zinc-700 text-zinc-300 rounded-lg hover:bg-zinc-800 flex items-center justify-center gap-2">
                    <Edit size={16} /> Edit User
                  </button>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Statistics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-white">{user.stats?.total_instances || 0}</p>
                  <p className="text-zinc-400 text-sm">Total Instances</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-400">{user.stats?.running_instances || 0}</p>
                  <p className="text-zinc-400 text-sm">Running</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-zinc-400">{user.stats?.stopped_instances || 0}</p>
                  <p className="text-zinc-400 text-sm">Stopped</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-cyan-400">{user.stats?.total_messages || 0}</p>
                  <p className="text-zinc-400 text-sm">Messages</p>
                </div>
              </div>
            </div>
          </div>

          {/* User's Instances */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">User's Instances</h3>
            {user.instances && user.instances.length > 0 ? (
              <div className="space-y-2">
                {user.instances.map((instance) => (
                  <div key={instance.id} className="bg-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Server size={20} className={instance.status === 'running' ? 'text-emerald-400' : 'text-zinc-400'} />
                      <div>
                        <p className="text-white font-medium">{instance.name}</p>
                        <p className="text-zinc-500 text-sm">{instance.tier} • {instance.region} • {instance.ip || 'No IP'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${instance.status === 'running' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {instance.status}
                      </span>
                      <div className="flex gap-1">
                        {instance.status !== 'running' && (
                          <button onClick={() => handleInstanceAction(instance.id, 'start')} className="p-1.5 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30" title="Start">▶</button>
                        )}
                        {instance.status === 'running' && (
                          <button onClick={() => handleInstanceAction(instance.id, 'stop')} className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" title="Stop">■</button>
                        )}
                        <button onClick={() => handleInstanceAction(instance.id, 'restart')} className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30" title="Restart">↻</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-800/30 rounded-lg p-8 text-center text-zinc-500">
                <Server size={32} className="mx-auto mb-2 opacity-50" />
                No instances found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Superuser Modal Component
function CreateSuperuserModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/api/auth/admin/users/create-superuser/', formData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create superuser');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <UserPlus className="text-emerald-400" /> Create Superuser
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Full Name</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              placeholder="Min 8 characters"
            />
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-sm">
            <p className="font-medium">⚠️ Superuser Privileges</p>
            <p className="text-amber-400/70 mt-1">This user will have full access to all system features, including user management and instance control.</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-zinc-700 text-zinc-300 rounded-lg hover:bg-zinc-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-black font-medium rounded-lg flex items-center justify-center gap-2"
            >
              {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <UserPlus size={18} />}
              Create Superuser
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
